/* eslint-disable react/prop-types */
import React from "react";
import { Instagram, Youtube, Facebook } from "lucide-react";

// Mappa colori e nomi delle piattaforme (deve corrispondere a quella di SocialPage)
const PLATFORMS = {
  instagram: { label: "Instagram", color: "#E1306C", icon: Instagram },
  facebook: { label: "Facebook", color: "#1877F2", icon: Facebook },
  youtube: { label: "YouTube", color: "#FF0000", icon: Youtube },
  tiktok: { label: "TikTok", color: "#000000", icon: null },
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(Math.round(n));
};

const today = () => new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

/**
 * Report PDF per singolo cliente (brand Metricool).
 * Renderizzato in un div fuori-schermo con dimensioni fisse A4 (794x1123 px @ 96dpi).
 * Esposta tramite ref per essere catturata con html2canvas.
 */
export const SocialReportPDF = React.forwardRef(({ client, period }, ref) => {
  if (!client) return null;
  const periodLabel = period === 7 ? "Ultimi 7 giorni" : "Ultimi 30 giorni";
  const platformOrder = ["instagram", "facebook", "youtube", "tiktok"];
  const accountsByPlatform = platformOrder.map(p => client.accounts.find(a => a.platform === p)).filter(Boolean);

  return (
    <div
      ref={ref}
      style={{
        width: "794px",
        minHeight: "1123px",
        backgroundColor: "#ffffff",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        color: "#0f172a",
        padding: "40px",
        boxSizing: "border-box",
      }}
    >
      {/* Header con logo e branding */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #0f172a", paddingBottom: "20px", marginBottom: "30px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <img src="/diecixcento-mark.png" alt="" style={{ width: "56px", height: "56px", objectFit: "contain", background: "#000", padding: "6px", borderRadius: "10px" }} />
          <div>
            <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em" }}>Diecixcento Agency</div>
            <div style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase" }}>Social Media Report</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 600 }}>Generato il</div>
          <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>{today()}</div>
        </div>
      </div>

      {/* Cliente */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
        {client.picture && (
          <img src={client.picture} crossOrigin="anonymous" alt="" style={{ width: "72px", height: "72px", borderRadius: "16px", objectFit: "cover", border: "2px solid #e2e8f0" }} />
        )}
        <div>
          <div style={{ fontSize: "32px", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{client.name}</div>
          <div style={{ marginTop: "6px", fontSize: "13px", color: "#475569" }}>
            <span style={{ background: "#f1f5f9", padding: "3px 10px", borderRadius: "999px", fontWeight: 600 }}>{periodLabel}</span>
            <span style={{ marginLeft: "10px" }}>{client.accounts.length} piattaforme collegate</span>
          </div>
        </div>
      </div>

      {/* KPI principali */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "28px" }}>
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)", color: "#fff", padding: "16px", borderRadius: "12px" }}>
          <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.7, fontWeight: 600 }}>Follower totali</div>
          <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{formatNumber(client.followers)}</div>
          {client.followers_growth !== 0 && (
            <div style={{ fontSize: "11px", marginTop: "4px", color: client.followers_growth > 0 ? "#86efac" : "#fca5a5", fontWeight: 700 }}>
              {client.followers_growth > 0 ? "▲" : "▼"} {formatNumber(Math.abs(client.followers_growth))} nel periodo
            </div>
          )}
        </div>
        <div style={{ background: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)", color: "#fff", padding: "16px", borderRadius: "12px" }}>
          <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.9, fontWeight: 600 }}>Visualizzazioni</div>
          <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{formatNumber(client.views)}</div>
          <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.9 }}>reels · post · video</div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)", color: "#fff", padding: "16px", borderRadius: "12px" }}>
          <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.9, fontWeight: 600 }}>Engagement medio</div>
          <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{(client.engagement || 0).toFixed(2)}%</div>
          <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.9 }}>{formatNumber(client.interactions)} interazioni</div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "#fff", padding: "16px", borderRadius: "12px" }}>
          <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.9, fontWeight: 600 }}>Contenuti</div>
          <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px" }}>{formatNumber(client.media_count)}</div>
          <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.9 }}>pubblicati</div>
        </div>
      </div>

      {/* Sezione per-piattaforma */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ display: "inline-block", width: "4px", height: "16px", background: "#0f172a", borderRadius: "2px" }} />
          Performance per piattaforma
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
          {accountsByPlatform.map(a => {
            const meta = PLATFORMS[a.platform];
            const Icon = meta?.icon;
            return (
              <div key={a.account_id} style={{ border: `2px solid ${meta?.color}30`, background: `${meta?.color}08`, borderRadius: "12px", padding: "14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                  {Icon ? <Icon size={18} color={meta?.color} /> : <span style={{ display: "inline-block", width: "18px", height: "18px", borderRadius: "4px", background: meta?.color, color: "#fff", fontSize: "10px", fontWeight: 800, textAlign: "center", lineHeight: "18px" }}>TT</span>}
                  <span style={{ fontSize: "14px", fontWeight: 700, color: meta?.color }}>{meta?.label}</span>
                  {a.username && <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "auto" }}>@{a.username}</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", fontWeight: 600 }}>Follower</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>{a.followers_count > 0 ? formatNumber(a.followers_count) : "n/d"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", fontWeight: 600 }}>Views</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>{formatNumber(a.views || 0)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", fontWeight: 600 }}>Engagement</div>
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{(a.engagement || 0).toFixed(2)}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", fontWeight: 600 }}>Interazioni</div>
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{formatNumber(a.interactions || 0)}</div>
                  </div>
                </div>
                <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: `1px dashed ${meta?.color}40`, fontSize: "11px", color: "#475569" }}>
                  <strong style={{ color: meta?.color }}>{a.media_count || 0}</strong> contenuti pubblicati nel periodo
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Meta Ads section, se brand ha ads */}
      {client.has_meta_ads && client.meta_ads && (client.meta_ads.spend > 0 || client.meta_ads.leads > 0) && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ display: "inline-block", width: "4px", height: "16px", background: "#10b981", borderRadius: "2px" }} />
            Performance Meta Ads
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "10px" }}>
            {[
              { label: "Lead", value: client.meta_ads.leads, color: "#10b981" },
              { label: "Spesa", value: `€${client.meta_ads.spend.toFixed(2)}`, color: "#0f172a" },
              { label: "CPL", value: `€${client.meta_ads.cpl.toFixed(2)}`, color: "#f59e0b" },
              { label: "CPC", value: `€${client.meta_ads.cpc.toFixed(2)}`, color: "#2563eb" },
              { label: "CPM", value: `€${client.meta_ads.cpm.toFixed(2)}`, color: "#ec4899" },
            ].map(k => (
              <div key={k.label} style={{ background: `${k.color}10`, border: `1px solid ${k.color}30`, padding: "10px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.1em", color: k.color, fontWeight: 700 }}>{k.label}</div>
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "4px" }}>{k.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ position: "absolute", bottom: "20px", left: "40px", right: "40px", borderTop: "1px solid #e2e8f0", paddingTop: "12px", display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#94a3b8" }}>
        <span>Dati aggregati da Metricool · Diecixcento Agency © {new Date().getFullYear()}</span>
        <span style={{ fontWeight: 600 }}>Report generato automaticamente</span>
      </div>
    </div>
  );
});
SocialReportPDF.displayName = "SocialReportPDF";

/**
 * Funzione utility che cattura un nodo DOM e genera un PDF A4 da scaricare.
 */
export async function downloadElementAsPDF(element, filename = "report.pdf") {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  });
  const imgData = canvas.toDataURL("image/png");
  // A4 in jsPDF mm: 210 x 297
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();
  // Mantieni l'aspect ratio del canvas
  const imgW = pdfW;
  const imgH = (canvas.height * pdfW) / canvas.width;
  if (imgH <= pdfH) {
    pdf.addImage(imgData, "PNG", 0, 0, imgW, imgH);
  } else {
    // Multi-pagina se troppo lungo
    let position = 0;
    const pageImgH = pdfH;
    while (position < imgH) {
      pdf.addImage(imgData, "PNG", 0, -position, imgW, imgH);
      position += pageImgH;
      if (position < imgH) pdf.addPage();
    }
  }
  pdf.save(filename);
}
